import useGetSoNetMailInfo from '@/hooks/api/useGetSoNetMailInfo';
import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import NotificationInfoState from '@/states/notificationInfoState';
import SoNetMailInfoState from '@/states/soNetMailInfoState';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import { useEffect } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import ExternalLink from '../atoms/ExternalLink';
import Heading from '../atoms/Heading';

type Props = {
  anchor: string;
  // eslint-disable-next-line react/require-default-props
  isMobile?: boolean;
};

/**
 * お知らせ表示コンポーネント
 * マウント時に通知情報API・So-netメール情報APIを呼び出し、
 * ユーザー向けの案内メッセージを一覧表示する。
 * So-netメールが停止/削除された場合は重要通知を出し、メール喪失を周知する。
 *
 * @param anchor スクロールターゲットとなるHTML id属性
 * @param isMobile モバイル表示の場合true（So-netメール情報の取得をスキップする）
 */
const News = ({ anchor, isMobile = false }: Props) => {
  // state
  const setAlert = useSetRecoilState(alertState);
  const [notificationsInfo, setNotificationsInfo] = useRecoilState(NotificationInfoState);
  const [soNetMailInfo, setSoNetMailInfo] = useRecoilState(SoNetMailInfoState);

  // hooks
  const { startLoading, finishLoading } = useSetLoading();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();

  // API呼び出しhooks
  const { execute: getSoNetMailInfo } = useGetSoNetMailInfo();

  useEffect(() => {
    (async () => {
      const apiUrl = ApiUrl.Read.NOTIFICATION_INFO_V2;
      const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
      startLoading(apiUrlWithServiceId);
      const headers = await getSigV4HeadersForGet(apiUrlWithServiceId);
      await api
        .get(apiUrlWithServiceId, headers)
        .then(({ data }) => {
          const { isError } = commonResponseCodeHandling({
            responseCode: data.response_code,
            isTransitionTop: true,
          });

          if (isError) return;

          setNotificationsInfo(data);
        })
        .catch((error: any) => {
          // ログイントークンの有効期限が切れていたら、ログイン画面に遷移
          if (isExpiredToken(error)) {
            sessionStorage.clear();
            window.location.href = url('/login');
            return;
          }

          commonErrorHandling({ error, isTransitionTop: true });
        })
        .finally(() => finishLoading(apiUrlWithServiceId));
    })();

    if (!isMobile) {
      callAPIGetSoNetMailInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // So-net メールシステム情報取得API呼び出し
  const callAPIGetSoNetMailInfo = async () => {
    try {
      startLoading(ApiUrl.Read.SONET_MAIL_INFO);
      const { data } = await getSoNetMailInfo();
      // 取得した値をセット
      setSoNetMailInfo({ ...data });
    } catch (error: any) {
      if (error.response.status === 403) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.SONET_MAIL_INFO);
    }
  };

  return (
    <div className='m-box-02 notification' id={anchor}>
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='bell'>
          お知らせ
        </Heading>
        {notificationsInfo.notifications.length === 0 &&
        soNetMailInfo.status !== '1' &&
        soNetMailInfo.status !== '3' ? (
          <p className='m-text' data-testid='not_notification'>
            現在お知らせはございません。
          </p>
        ) : (
          notificationsInfo.notifications.map((content) => (
            <div key={content.notification_text}>
              <div className='m-text' data-testid='notification_text'>
                <div dangerouslySetInnerHTML={{ __html: content.notification_text }} />
              </div>
              {content.detail_link_url && (
                <div className='m-linkTxt'>
                  <ExternalLink href={content.detail_link_url} ariaLabel='詳細はこちら'>
                    {content.detail_link_url}
                  </ExternalLink>
                </div>
              )}
            </div>
          ))
        )}
        {(soNetMailInfo.status === '1' || soNetMailInfo.status === '3') && (
          // So-netメールアドレスが停止/削除の場合、以下のお知らせを表示する
          <div>
            <div className='m-text' data-testid='notification_text'>
              <div>
                <b>【重要】</b>
                <p>
                  お客さまのSo-netメールアドレスは、一定期間ご利用がなかったか、もしくは回線プランをご解約済のため削除させていただきました。
                </p>
                <p>詳しくは下記URLのお知らせページをご確認ください。</p>
              </div>
            </div>
            <div className='m-linkTxt'>
              <ExternalLink
                href='https://www.nuro.jp/news_release/20230912/'
                ariaLabel='詳しくはこちら'
              >
                https://www.nuro.jp/news_release/20230912/
              </ExternalLink>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
